import type { StatsParams } from '@empathybroker/tsclient-stats/dist/models/params/stats-params'
import type { ActionContext } from 'vuex'

import type {
  ActionClass,
  AnalyticsState,
  ContextState,
  DateFilterSelectedValues,
  IdData,
  PeriodsData,
  RegularFilter,
} from '../../../models'
import type { ContextProvider } from '../../../providers'
// @ts-strict-ignore
import { StatsReportsIdsEnum } from '@empathybroker/tsclient-stats/dist/enums/stats-reports-ids.enum'

import { CONTAINER } from '../../../config'

import { TYPES } from '../../../config/inversify.types'
import { CompareFilterEnum, FilterIdEnum } from '../../../enums'

import { ANALYTICS_CONSTS } from '../analytics.consts'

export class LoadDistDataAction implements ActionClass {
  static async execute(
    this: void,
    { dispatch, commit, state }: ActionContext<AnalyticsState, any>,
    payload?: string | { selectedId?: string; filters?: StatsParams['filters'] },
  ): Promise<void> {
    const selectedId = typeof payload === 'string' ? payload : payload?.selectedId
    const filters = typeof payload === 'object' ? payload?.filters : undefined

    let distData: PeriodsData
    if (!selectedId) {
      distData = state.data.overall.dist
    } else {
      const selectedIdData: IdData = state.data.ids[selectedId]
      if (selectedIdData) {
        distData = selectedIdData.dist
      }
    }
    const reportId = LoadDistDataAction.getReportId(selectedId)
    const reportParams: any = {
      ...(filters && Object.keys(filters).length && { filters }),
    }
    if (selectedId) {
      reportParams.terms = selectedId
    }
    try {
      const newDistData: Partial<PeriodsData> = {}
      if (!distData?.currentPeriod) {
        newDistData.currentPeriod = await dispatch(ANALYTICS_CONSTS.actions.fetchStatsReport, {
          id: reportId,
          params: reportParams,
        })
      }
      const periodToCompare = LoadDistDataAction.getPeriodToCompare()
      if (!!periodToCompare && !distData?.[periodToCompare]) {
        const periodToCompareDates = LoadDistDataAction.getPeriodToCompareDates(periodToCompare)
        newDistData[periodToCompare] = await dispatch(ANALYTICS_CONSTS.actions.fetchStatsReport, {
          id: reportId,
          params: {
            ...reportParams,
            startDate: periodToCompareDates[0],
            endDate: periodToCompareDates[1],
          },
        })
      }
      if (Object.keys(newDistData).length) {
        commit(ANALYTICS_CONSTS.mutations.setDistData, {
          data: newDistData,
          selectedId,
        })
      }
    } catch (error) {
      throw error
    }
  }

  static getPeriodToCompare(): CompareFilterEnum {
    const compareFilter = this.getContext().filters[FilterIdEnum.COMPARE] as RegularFilter
    return (compareFilter?.selectedValues[0] || undefined) as CompareFilterEnum
  }

  static getPeriodToCompareDates(periodToCompare: CompareFilterEnum): Date[] {
    const periodDates = this.getContext().filters[FilterIdEnum.DATE]
      .selectedValues as DateFilterSelectedValues
    return {
      [CompareFilterEnum.PREVIOUS_PERIOD]: () => {
        const endDate = new Date(periodDates[0])
        const startDate = new Date(endDate.getTime() - (periodDates[1] - periodDates[0]))
        return [startDate, endDate]
      },
      [CompareFilterEnum.YEAR_OVER_YEAR]: () => {
        const endDate = new Date(periodDates[1])
        endDate.setUTCFullYear(endDate.getUTCFullYear() - 1)
        const startDate = new Date(endDate.getTime() - (periodDates[1] - periodDates[0]))
        return [startDate, endDate]
      },
    }[periodToCompare]()
  }

  private static getReportId(selectedId?: string): StatsReportsIdsEnum {
    const periodDates = this.getContext().filters[FilterIdEnum.DATE]
      .selectedValues as DateFilterSelectedValues
    const auxDate = new Date(periodDates[0])
    auxDate.setUTCDate(auxDate.getUTCDate() + 1)
    const loadHourlyDist = periodDates[1] <= auxDate.getTime()
    if (selectedId) {
      return loadHourlyDist ? StatsReportsIdsEnum.HOURLY_QUERIES : StatsReportsIdsEnum.DAILY_QUERIES
    }
    return loadHourlyDist ? StatsReportsIdsEnum.HOURLY_SEARCH : StatsReportsIdsEnum.DAILY_SEARCH
  }

  private static getContext(): ContextState {
    return CONTAINER.get<ContextProvider>(TYPES.ContextProvider)()
  }
}
